import { useEffect, useState, useRef } from 'react';
import { X, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import type { TourStep, TourPlacement, Tour } from '../types';

interface TourTooltipProps {
  step: TourStep;
  tour: Tour;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}

interface Position {
  top: number;
  left: number;
}

const OFFSET = 16;

function computePosition(
  targetSelector: string | null,
  placement: TourPlacement,
  tooltipRef: React.RefObject<HTMLDivElement | null>
): Position {
  if (!targetSelector || !tooltipRef.current) {
    // Center in viewport
    const tw = tooltipRef.current?.offsetWidth ?? 360;
    const th = tooltipRef.current?.offsetHeight ?? 200;
    return {
      top: Math.max(16, (window.innerHeight - th) / 2),
      left: Math.max(16, (window.innerWidth - tw) / 2),
    };
  }

  const el = document.querySelector(`[data-tour="${targetSelector}"]`);
  if (!el) {
    // Element not found — center in viewport
    const tw2 = tooltipRef.current?.offsetWidth ?? 360;
    const th2 = tooltipRef.current?.offsetHeight ?? 200;
    return {
      top: Math.max(16, (window.innerHeight - th2) / 2),
      left: Math.max(16, (window.innerWidth - tw2) / 2),
    };
  }

  const rect = el.getBoundingClientRect();
  // If element is hidden (display:none), treat as not found — center in viewport
  if (rect.width === 0 && rect.height === 0) {
    const tw2 = tooltipRef.current?.offsetWidth ?? 360;
    const th2 = tooltipRef.current?.offsetHeight ?? 200;
    return {
      top: Math.max(16, (window.innerHeight - th2) / 2),
      left: Math.max(16, (window.innerWidth - tw2) / 2),
    };
  }
  const tw = tooltipRef.current.offsetWidth;
  const th = tooltipRef.current.offsetHeight;

  let top = 0;
  let left = 0;

  switch (placement) {
    case 'bottom':
      top = rect.bottom + OFFSET;
      left = rect.left + rect.width / 2 - tw / 2;
      break;
    case 'top':
      top = rect.top - th - OFFSET;
      left = rect.left + rect.width / 2 - tw / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - th / 2;
      left = rect.left - tw - OFFSET;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - th / 2;
      left = rect.right + OFFSET;
      break;
    default:
      top = rect.bottom + OFFSET;
      left = rect.left + rect.width / 2 - tw / 2;
  }

  // Clamp to viewport
  left = Math.max(12, Math.min(left, window.innerWidth - tw - 12));
  top = Math.max(12, Math.min(top, window.innerHeight - th - 12));

  return { top, left };
}

export function TourTooltip({
  step,
  tour,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
}: TourTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });

  useEffect(() => {
    // Scroll target into view if needed (only if element is visible)
    if (step.targetSelector) {
      const el = document.querySelector(`[data-tour="${step.targetSelector}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }

    // Small delay so scrolling finishes + tooltip rendered
    const timer = setTimeout(() => {
      setPosition(
        computePosition(step.targetSelector, step.placement ?? 'bottom', tooltipRef)
      );
    }, 100);

    const handleResize = () => {
      setPosition(
        computePosition(step.targetSelector, step.placement ?? 'bottom', tooltipRef)
      );
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [step]);

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const isModal = !step.targetSelector;

  return (
    <div
      ref={tooltipRef}
      className="fixed z-[9999] w-[340px] max-w-[calc(100vw-24px)] animate-in fade-in slide-in-from-bottom-2 duration-200"
      style={{ top: position.top, left: position.left }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-ds-card border border-ds-border rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <div className="flex items-center gap-2">
            {isModal && <Sparkles className="w-4 h-4 text-ds-gold" />}
            <span className="text-[10px] uppercase tracking-widest text-ds-gold font-semibold">
              {tour.name}
            </span>
          </div>
          <button
            onClick={onSkip}
            className="p-1 rounded-lg text-ds-gray-3 hover:text-ds-gray-1 hover:bg-ds-gray-4/50 transition-colors"
            aria-label="Skip tour"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 pb-3">
          <h3 className="font-display text-lg font-semibold text-ds-gray-1 mb-1">
            {step.title}
          </h3>
          <p className="text-sm text-ds-gray-2 leading-relaxed">{step.description}</p>
        </div>

        {/* Footer: progress + nav */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-ds-border bg-ds-black/30">
          {/* Progress dots */}
          <div className="flex items-center gap-1.5">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === stepIndex
                    ? 'bg-ds-red'
                    : i < stepIndex
                    ? 'bg-ds-gray-3'
                    : 'bg-ds-gray-4'
                }`}
              />
            ))}
            <span className="text-[10px] text-ds-gray-3 ml-2">
              {stepIndex + 1}/{totalSteps}
            </span>
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                onClick={onPrev}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-ds-gray-2 hover:text-ds-gray-1 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-3 h-3" />
                Back
              </button>
            )}
            <button
              onClick={onNext}
              className="flex items-center gap-1 px-4 py-1.5 text-xs font-semibold text-white bg-ds-red hover:bg-ds-red-hover rounded-lg transition-colors"
            >
              {isLast ? 'Finish' : 'Next'}
              {!isLast && <ArrowRight className="w-3 h-3" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
